import { Box, Divider, Drawer, useTheme } from '@mui/material';
import React, { useContext } from 'react';
import { constants } from '../../constants/constants';
import { SidebarStateContext } from '../../providers/MainProviders';
import SidebarHeader from './SidebarHeader/SidebarHeader';
import SidebarNav from './SidebarNav/SidebarNav';
import SidebarNavItem from './SidebarNavItem/SidebarNavItem';
import {
  AdminPanelSettings,
  ArrowCircleUp,
  Campaign,
  Dashboard as DashboardIcon,
  Explore,
  Forum,
  Google,
  Key,
  Label,
  ListAlt,
  LocationOn,
  ManageSearch,
  MarkChatRead,
  MonitorHeart,
  PersonSearch,
  Poll,
  PsychologyAlt,
  Search,
  SettingsInputAntenna,
  Speed,
  StarHalf,
} from '@mui/icons-material';
import SidebarFooter from './SidebarFooter/SidebarFooter';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AuthContext } from '../../providers/AuthProvider';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import DateUtils from '../../shared/DateUtils';

const CustomDivider = styled(Divider)``;

const Sidebar = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { expanded } = useContext(SidebarStateContext);
  const { hasRole, validUntil } = useContext(AuthContext);

  const vu = validUntil();
  const days = DateUtils.getDaysUntil(vu);

  const childElementStyle = {
    paddingLeft: theme.spacing(4),
  };

  const paperProps = {
    width: expanded
      ? constants.components.SIDEBAR_EXPANDED_WIDTH
      : constants.components.SIDEBAR_COLLAPSED_WIDTH,
    display: 'flex',
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    '-ms-overflow-style': 'none' /* Hide scrollbar on IE and Edge */,
    scrollbarWidth: 'none' /* Hide scrollbar on Firefox */,
    '&::-webkit-scrollbar': {
      display: 'none' /* Hide scrollbar on Safari + Chrome */,
    },
  };

  return (
    <Drawer
      PaperProps={{ sx: paperProps }}
      variant="permanent"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, height: '100%' }}>
        <SidebarHeader />
        <Box sx={{ flexGrow: '1', overflowY: 'auto' }}>
          <SidebarNav>
            {hasRole('can-use-dashboard') && (
              <>
                <CustomDivider />
                <SidebarNavItem
                  text={t('dashboard')}
                  to="/dashboard"
                  tooltip={t('dashboard')}
                  icon={<Poll fontSize="small" />}
                />
              </>
            )}
            <SidebarNavItem
              text={t('nav_dashboard')}
              to="/dashboard"
              tooltip={t('nav_dashboard')}
              icon={<DashboardIcon fontSize="small" />}
              style={{ display: 'none' }}
            />
            <CustomDivider />
            <SidebarNavItem
              text={t('nav_monitoring')}
              to=""
              icon={<MonitorHeart fontSize="small" />}
            >
              <SidebarNavItem
                text={t('nav_platforms')}
                to="/monitoring/platforms"
                tooltip={t('nav_platforms')}
                icon={<ListAlt fontSize="small" />}
                style={childElementStyle}
              />
              <SidebarNavItem
                text={t('nav_reviews')}
                to="/monitoring/reviews"
                tooltip={t('nav_reviews')}
                icon={<StarHalf fontSize="small" />}
                style={childElementStyle}
              />
              <SidebarNavItem
                text={t('nav_keywords')}
                to="/monitoring/keywords"
                tooltip={t('nav_keywords')}
                icon={<Key fontSize="small" />}
                style={childElementStyle}
              />
              {hasRole('can-use-ai-search') && (
                <SidebarNavItem
                  text={t('nav_ai_search')}
                  to="/monitoring/ai-search"
                  tooltip={t('nav_ai_search')}
                  icon={<Search fontSize="small" />}
                  style={childElementStyle}
                />
              )}
              {hasRole('can-use-aireviews') && (
                <SidebarNavItem
                  text={t('nav_analysis')}
                  to="/monitoring/analysis"
                  tooltip={t('nav_analysis')}
                  icon={<PsychologyAlt fontSize="small" />}
                  style={childElementStyle}
                />
              )}
              <SidebarNavItem
                text={t('nav_groups')}
                to="/monitoring/groups"
                tooltip={t('nav_groups')}
                icon={<Label fontSize="small" />}
                style={childElementStyle}
              />
            </SidebarNavItem>
            <CustomDivider />
            <SidebarNavItem
              text={t('nav_google_my_business')}
              to=""
              icon={<Google fontSize="small" />}
            >
              <SidebarNavItem
                text={t('nav_locations')}
                to="/googlemybusiness/locations"
                tooltip={t('nav_locations')}
                icon={<LocationOn fontSize="small" />}
                style={childElementStyle}
              />
              <SidebarNavItem
                text={t('nav_reviews')}
                to="/googlemybusiness/reviews"
                tooltip={t('nav_reviews')}
                icon={<StarHalf fontSize="small" />}
                style={childElementStyle}
              />
              {hasRole('can-use-history') && (
                <SidebarNavItem
                  text={t('location_history')}
                  to={'/googlemybusiness/location-history'}
                  tooltip={t('location_history')}
                  icon={<ManageSearchIcon fontSize="small" />}
                  style={childElementStyle}
                />
              )}
              <SidebarNavItem
                text={t('nav_evaluation')}
                to="/googlemybusiness/evaluation"
                tooltip={t('nav_evaluation')}
                icon={<ManageSearch fontSize="small" />}
                style={{ ...childElementStyle, display: 'none' }}
              />
            </SidebarNavItem>
            <CustomDivider />
            <SidebarNavItem
              text={t('nav_research')}
              to=""
              icon={<Explore fontSize="small" />}
            >
              <SidebarNavItem
                text={t('nav_benchmarking')}
                to="/research/benchmarking"
                tooltip={t('nav_benchmarking')}
                icon={<Speed fontSize="small" />}
                style={childElementStyle}
              />
              {hasRole('can-use-survey') && (
                <SidebarNavItem
                  text={t('nav_survey_tool')}
                  to="/research/surveytool/guide"
                  tooltip={t('nav_survey_tool')}
                  icon={<Poll fontSize="small" />}
                  style={childElementStyle}
                />
              )}
            </SidebarNavItem>
            {hasRole('can-use-ai') && (
              <>
                <CustomDivider />
                <SidebarNavItem
                  text={t('nav_community_management')}
                  to=""
                  icon={<PersonSearch fontSize="small" />}
                >
                  <SidebarNavItem
                    text={t('nav_commentings')}
                    to="/communitymanagement/commentings"
                    tooltip={t('nav_commentings')}
                    icon={<Forum fontSize="small" />}
                    style={childElementStyle}
                  />
                  <>
                    <SidebarNavItem
                      text={t('nav_ai_commentgenerator')}
                      to="/communitymanagement/ai-commentgenerator"
                      tooltip={t('nav_ai_commentgenerator')}
                      icon={<MarkChatRead fontSize="small" />}
                      style={childElementStyle}
                    />
                  </>
                </SidebarNavItem>
              </>
            )}
            {hasRole('can-view-trustpage') && (
              <>
                <CustomDivider />
                <SidebarNavItem
                  text={t('nav_visibility')}
                  to=""
                  icon={<SettingsInputAntenna fontSize="small" />}
                >
                  <SidebarNavItem
                    text={t('trustpage')}
                    to="/visibility/trustpages"
                    tooltip={t('trustpage')}
                    icon={<Campaign fontSize="small" />}
                    style={childElementStyle}
                  />
                </SidebarNavItem>
              </>
            )}
            <CustomDivider />
            <SidebarNavItem
              text={t('nav_administration')}
              to="/administration"
              tooltip={t('nav_administration')}
              icon={<AdminPanelSettings fontSize="small" />}
            />
            <CustomDivider />
          </SidebarNav>
        </Box>
        <SidebarFooter />
      </Box>
    </Drawer>
  );
};

export default Sidebar;
